import {
    agentRunLedgerService,
    hasCloudAuditRunPaginationMetadata,
} from '@common/api/agentRunLedgerService';
import { type LocaleCode, type Translate, useTranslation } from '@client-app/i18n';
import {
    ArrowRight,
    CaretDown,
    ChatCircleText,
    Check,
    CircleNotch,
    MagnifyingGlass,
    WarningCircle,
    X,
    XCircle,
} from '@phosphor-icons/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PaginationFooter, { getVisiblePaginationPages } from '@/components/Pagination/PaginationFooter';
import { BustlyLoadingAnimation } from '@/components/GlobalLoading';
import { getUserAvatarUrl, useAuth } from '@/contexts/AuthContext';
import { getBrandIcon } from '../Settings/components/integrations/IntegrationCard';
import {
    adaptCloudAgentRunAuditContent,
    adaptCloudAgentRunAuditConversation,
    adaptCloudAgentRunAuditEventListToRunList,
    adaptCloudAgentRunAuditRunList,
    getVisibleCloudAgentRunAuditConversationMessages,
    type CloudAgentRunAuditContentView,
    type CloudAgentRunAuditConversationMessageView,
    type CloudAgentRunAuditConversationView,
    type CloudAgentRunAuditListView,
    type CloudAgentRunAuditRunView,
    type CloudAgentRunAuditTimelineEventView,
} from '@/view-models/cloud-agent-run-audit';
import { canViewCloudAgentRunAudit } from './cloudAgentRunAuditAccess';
import {
    getAuditApplicationDisplayName,
    getAuditApplicationFilterValues,
    getRunAuditApplications,
} from './auditLogApplications';
import {
    DEFAULT_AUDIT_LOG_TIME_RANGE,
    doesAuditRunOverlapTimeRange,
    getAuditLogTimeWindow,
    resolveAuditLogTimeZone,
    type AuditLogTimeRange,
} from './auditLogTimeRange';

type AuditPagination =
    | { kind: 'cursor'; nextCursor: string | null }
    | { kind: 'local' };

type AuditPageState =
    | { kind: 'loading' }
    | { kind: 'ready'; data: CloudAgentRunAuditListView; pagination: AuditPagination }
    | { kind: 'forbidden' }
    | { kind: 'workspace-unavailable' }
    | { kind: 'error'; detail?: string };

type ContentState =
    | { kind: 'idle' }
    | { kind: 'loading'; eventId: string }
    | { kind: 'ready'; eventId: string; content: CloudAgentRunAuditContentView }
    | { kind: 'forbidden'; eventId: string }
    | { kind: 'unavailable'; eventId: string }
    | { kind: 'error'; eventId: string };

type ConversationState =
    | { kind: 'idle' }
    | { kind: 'loading'; runId: string }
    | { kind: 'ready'; runId: string; conversation: CloudAgentRunAuditConversationView }
    | { kind: 'forbidden'; runId: string }
    | { kind: 'unavailable'; runId: string }
    | { kind: 'error'; runId: string };

type AuditDrawerTab = 'conversation' | 'timeline';
type AuditFilterKind = 'application' | 'initiator' | 'time';

interface AuditFilterOption {
    label: string;
    platform?: string;
    value: string;
}

interface AuditTimeRangeOption {
    label: string;
    value: AuditLogTimeRange;
}

const auditDrawerTabs: Array<{ value: AuditDrawerTab; label: string }> = [
    { value: 'timeline', label: 'website.auditLog.timeline' },
    { value: 'conversation', label: 'website.auditLog.originalConversation' },
];

const AUDIT_FILTER_ALL = '__all__';
const AUDIT_FILTER_NONE = '__none__';
const AUDIT_LOG_PAGE_SIZE = 10;
const LEGACY_AUDIT_RUN_LIMIT = 200;
const FALLBACK_AUDIT_EVENT_LIMIT = 100;
const AUDIT_TOOLTIP_CONTENT_CLASS = 'z-[20000] max-w-[420px] rounded-lg bg-[#1A162F] px-2.5 py-1.5 text-[12px] font-normal leading-5 text-white shadow-[0_6px_16px_rgba(26,22,47,0.16)] break-words';
const MAX_VISIBLE_PLATFORM_ITEMS = 5;

const getHttpStatus = (error: unknown): number | null => {
    if (!error || typeof error !== 'object') return null;
    const response = (error as { response?: unknown }).response;
    if (!response || typeof response !== 'object') return null;
    const status = (response as { status?: unknown }).status;
    return typeof status === 'number' ? status : null;
};

const getApiErrorCode = (error: unknown): string | null => {
    if (!error || typeof error !== 'object') return null;
    const code = (error as { code?: unknown }).code;
    if (typeof code === 'string') return code;
    const response = (error as { response?: unknown }).response;
    if (!response || typeof response !== 'object') return null;
    const responseCode = (response as { code?: unknown }).code;
    return typeof responseCode === 'string' ? responseCode : null;
};

const shouldFallbackToEventList = (error: unknown): boolean => {
    const status = getHttpStatus(error);
    if (status === 404 || status === 405) return true;
    const code = getApiErrorCode(error);
    if (code === 'ECONNABORTED') return true;
    return code === '404' || code === '405' || code === 'NOT_FOUND';
};

const describeLoadError = (error: unknown): string | undefined => {
    if (import.meta.env.MODE !== 'internal' && import.meta.env.VITE_DEBUG !== 'true') return undefined;
    const status = getHttpStatus(error);
    const code = getApiErrorCode(error);
    if (status && code) return `HTTP ${status} / ${code}`;
    if (status) return `HTTP ${status}`;
    if (code) return code;
    return undefined;
};

const getIntlLocale = (locale: LocaleCode): string => (locale === 'zh-CN' ? 'zh-CN' : 'en-US');

const formatDateTime = (
    value: string | null,
    locale: LocaleCode,
    timeZone = resolveAuditLogTimeZone(),
): string => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';

    return new Intl.DateTimeFormat(getIntlLocale(locale), {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone,
    }).format(date);
};

const formatStepTime = (
    value: string | null,
    locale: LocaleCode,
    timeZone = resolveAuditLogTimeZone(),
): string => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';

    return new Intl.DateTimeFormat(getIntlLocale(locale), {
        hour: 'numeric',
        hour12: true,
        minute: '2-digit',
        timeZone,
    }).format(date);
};

const formatPayload = (payload: unknown): string => {
    try {
        return JSON.stringify(payload, null, 2);
    } catch {
        return String(payload);
    }
};

const isCompletedStatus = (status: string): boolean => /已完成|completed|success|succeeded/i.test(status);
const isFailedStatus = (status: string): boolean => /失败|failed|error/i.test(status);

const statusBadgeClassName = (status: string): string => (
    isCompletedStatus(status)
        ? 'bg-[#EBF8F0] text-[#177A45]'
        : isFailedStatus(status)
            ? 'bg-red-50 text-red-700'
            : 'bg-blue-50 text-blue-700'
);

const canRequestConversation = (run: CloudAgentRunAuditRunView): boolean => (
    run.conversationAvailability === 'available' || run.conversationAvailability === 'unknown'
);

const uniqueSorted = (values: Array<string | null | undefined>): string[] => (
    Array.from(new Set(values.map((value) => String(value || '').trim()).filter(Boolean)))
        .sort((first, second) => first.localeCompare(second))
);

const getRunActionSummary = (run: CloudAgentRunAuditRunView, t: Translate): string => (
    t('website.auditLog.readWriteCount', { read: run.readCount, write: run.writeCount })
);

const getDisplayMember = (
    run: CloudAgentRunAuditRunView,
    fallbackName: string,
): string => run.actorDisplayName || fallbackName || '-';

const includesQuery = (value: string | null | undefined, query: string): boolean => (
    !query || String(value || '').toLowerCase().includes(query)
);

const isAllFilterSelected = (values: string[]): boolean => (
    values.length === 0 || values.includes(AUDIT_FILTER_ALL)
);

const selectedFilterValues = (values: string[]): string[] => (
    isAllFilterSelected(values) ? [] : values.filter((value) => value !== AUDIT_FILTER_NONE)
);

const matchesMultiSelect = (candidateValues: string[], selectedValues: string[]): boolean => {
    if (isAllFilterSelected(selectedValues)) return true;
    const activeValues = selectedFilterValues(selectedValues);
    if (!activeValues.length) return false;
    return candidateValues.some((value) => activeValues.includes(value));
};

const filterRuns = (
    runs: CloudAgentRunAuditRunView[],
    query: string,
    selectedTimeRange: AuditLogTimeRange,
    selectedApplications: string[],
    selectedInitiators: string[],
    timeZone: string,
): CloudAgentRunAuditRunView[] => {
    const normalizedQuery = query.trim().toLowerCase();

    return runs.filter((run) => (
        doesAuditRunOverlapTimeRange(run, selectedTimeRange, new Date(), timeZone)
        && matchesMultiSelect(getRunAuditApplications(run), selectedApplications)
        && matchesMultiSelect(run.actorDisplayName ? [run.actorDisplayName] : [], selectedInitiators)
        && [
            run.title,
            run.sourceTask,
            run.actorDisplayName,
            run.agentLabel,
            run.statusLabel,
            ...getRunAuditApplications(run),
            ...run.timeline.flatMap((event) => [
                event.actionLabel,
                event.description,
                event.operationLabel,
                event.resourceLabel,
                event.connectionDisplayName,
            ]),
        ].some((value) => includesQuery(value, normalizedQuery))
    ));
};

const AuditTimeRangeDropdown: React.FC<{
    ariaLabel: string;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    onSelect: (value: AuditLogTimeRange) => void;
    options: AuditTimeRangeOption[];
    selectedValue: AuditLogTimeRange;
}> = ({ ariaLabel, isOpen, onOpenChange, onSelect, options, selectedValue }) => {
    const selectedOption = options.find((option) => option.value === selectedValue) || options[0];
    const displayLabel = selectedOption?.label || ariaLabel;

    return (
        <DropdownMenu.Root open={isOpen} onOpenChange={onOpenChange}>
            <DropdownMenu.Trigger asChild>
                <button
                    aria-label={`${ariaLabel}: ${displayLabel}`}
                    className="inline-flex h-9 w-[180px] items-center justify-between gap-3 rounded-[10px] border border-[#E8EBF3] bg-white px-3 py-1.5 text-left text-sm font-normal text-[#1A162F] transition-all hover:border-[#C8D0E2] focus:outline-none focus-visible:border-[#C8D0E2] focus-visible:ring-2 focus-visible:ring-[#1A162F]/15 data-[state=open]:border-[#C8D0E2] data-[state=open]:ring-2 data-[state=open]:ring-[#1A162F]/15"
                    type="button"
                >
                    <span className="min-w-0 truncate">{displayLabel}</span>
                    <CaretDown
                        className={clsx('shrink-0 text-[#8A93B2] transition-transform duration-200', isOpen && 'rotate-180')}
                        size={14}
                        weight="regular"
                    />
                </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    align="end"
                    className="z-[10002] w-[180px] origin-top overflow-hidden rounded-xl border border-[#E8EBF3] bg-white p-1 shadow-[0_16px_40px_rgba(26,22,47,0.12)] animate-in fade-in zoom-in-95 duration-100"
                    sideOffset={6}
                >
                    {options.map((option) => {
                        const isSelected = option.value === selectedValue;
                        return (
                            <DropdownMenu.Item
                                className={clsx(
                                    'flex cursor-pointer items-center justify-between gap-3 rounded-[8px] px-2.5 py-2 text-sm font-normal outline-none transition-colors hover:bg-[#F8F9FC] focus:bg-[#F8F9FC]',
                                    isSelected ? 'bg-[#F4F5F8] text-[#1A162F]' : 'text-[#666F8D] hover:text-[#1A162F]',
                                )}
                                key={option.value}
                                onSelect={() => onSelect(option.value)}
                            >
                                <span className="min-w-0 truncate">{option.label}</span>
                                {isSelected ? <Check className="shrink-0 text-[#1A162F]" size={14} weight="bold" /> : null}
                            </DropdownMenu.Item>
                        );
                    })}
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
};

const EmptyState: React.FC<{
    actionLabel?: string;
    body: string;
    icon?: React.ReactNode;
    onAction?: () => void;
    title: string;
}> = ({ actionLabel, body, icon, onAction, title }) => (
    <div className="flex min-h-[280px] flex-col items-center justify-center rounded-xl border border-dashed border-[#C8D0E2] bg-[#F8F9FC] px-6 py-12 text-center">
        {icon ? <div className="mb-3 flex h-8 w-8 items-center justify-center text-[#8A93B2]">{icon}</div> : null}
        <h3 className="text-base font-medium text-[#1A162F]">{title}</h3>
        <p className="mt-2 max-w-[420px] text-sm font-normal leading-6 text-[#666F8D]">{body}</p>
        {actionLabel && onAction ? (
            <button
                className="mt-4 inline-flex h-8 items-center rounded-lg border border-[#E8EBF3] bg-white px-3 text-[12px] font-medium text-[#1A162F] transition-colors hover:border-[#C8D0E2] hover:bg-[#F4F5F8] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20"
                onClick={onAction}
                type="button"
            >
                {actionLabel}
            </button>
        ) : null}
    </div>
);

const ErrorState: React.FC<{
    body: string;
    onRetry?: () => void;
    title: string;
    t: Translate;
}> = ({ body, onRetry, title, t }) => (
    <div className="flex min-h-[240px] flex-col items-center justify-center rounded-xl border border-red-100 bg-red-50 px-6 py-10 text-center">
        <WarningCircle size={22} weight="bold" className="mb-3 text-red-600" />
        <h3 className="text-base font-medium text-[#1A162F]">{title}</h3>
        <p className="mt-2 max-w-[420px] text-sm font-normal leading-6 text-red-700">{body}</p>
        {onRetry ? (
            <button
                className="mt-4 text-sm font-medium text-[#1A162F] underline underline-offset-4"
                onClick={onRetry}
                type="button"
            >
                {t('website.auditLog.tryAgain')}
            </button>
        ) : null}
    </div>
);

const AuditSkeletonBar: React.FC<{ className: string }> = ({ className }) => (
    <div className={clsx('animate-pulse bg-[#E8EDF5]', className)} />
);

const LoadingState: React.FC = () => (
    <div aria-busy="true" className="space-y-3">
        {Array.from({ length: 3 }).map((_, index) => (
            <article className="rounded-xl border border-[#E8EBF3] bg-white p-5" key={index}>
                <div className="flex items-center justify-between gap-4">
                    <div className="flex min-w-0 items-center gap-2">
                        <AuditSkeletonBar className="h-4 w-52 rounded-full" />
                        <AuditSkeletonBar className="h-6 w-16 rounded-full" />
                    </div>
                    <AuditSkeletonBar className="h-8 w-36 shrink-0 rounded-lg" />
                </div>
                <div className="mt-4 grid grid-cols-3 gap-x-8 gap-y-3.5 border-t border-[#F1F4F9] pt-4">
                    {Array.from({ length: 6 }).map((_, detailIndex) => (
                        <div key={detailIndex}>
                            <AuditSkeletonBar className="h-3 w-16 rounded-full" />
                            <AuditSkeletonBar
                                className={clsx(
                                    'mt-2 h-4 rounded-full',
                                    detailIndex % 3 === 0 ? 'w-28' : detailIndex % 3 === 1 ? 'w-20' : 'w-24',
                                )}
                            />
                        </div>
                    ))}
                </div>
                <div className="mt-3 space-y-2 border-t border-[#F1F4F9] pt-3">
                    {Array.from({ length: 2 }).map((_, logIndex) => (
                        <div className="flex items-center gap-2" key={logIndex}>
                            <AuditSkeletonBar className="h-3 w-12 rounded-full" />
                            <AuditSkeletonBar className="h-1.5 w-1.5 rounded-full" />
                            <AuditSkeletonBar className={clsx('h-3 rounded-full', logIndex === 0 ? 'w-56' : 'w-40')} />
                        </div>
                    ))}
                </div>
            </article>
        ))}
    </div>
);

const AuditTooltip: React.FC<{
    align?: 'center' | 'end' | 'start';
    children: React.ReactElement;
    content: string;
    side?: 'bottom' | 'left' | 'right' | 'top';
}> = ({ align = 'center', children, content, side = 'top' }) => {
    if (!content) return children;

    return (
        <TooltipPrimitive.Provider delayDuration={200}>
            <TooltipPrimitive.Root>
                <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
                <TooltipPrimitive.Portal>
                    <TooltipPrimitive.Content
                        align={align}
                        avoidCollisions
                        className={AUDIT_TOOLTIP_CONTENT_CLASS}
                        collisionPadding={8}
                        side={side}
                        sideOffset={8}
                        sticky="partial"
                    >
                        {content}
                    </TooltipPrimitive.Content>
                </TooltipPrimitive.Portal>
            </TooltipPrimitive.Root>
        </TooltipPrimitive.Provider>
    );
};

const useIsTextTruncated = (textRef: React.RefObject<HTMLElement>, text: string) => {
    const [isTruncated, setIsTruncated] = useState(false);

    const updateTruncationState = useCallback(() => {
        const element = textRef.current;
        if (!element) {
            setIsTruncated(false);
            return false;
        }

        const nextIsTruncated = (
            element.scrollWidth > element.clientWidth + 1
            || element.scrollHeight > element.clientHeight + 1
        );
        setIsTruncated((previous) => (previous === nextIsTruncated ? previous : nextIsTruncated));
        return nextIsTruncated;
    }, [textRef]);

    useEffect(() => {
        const element = textRef.current;
        if (!element) {
            setIsTruncated(false);
            return undefined;
        }

        updateTruncationState();
        const frameId = window.requestAnimationFrame(updateTruncationState);
        window.addEventListener('resize', updateTruncationState);

        return () => {
            window.cancelAnimationFrame(frameId);
            window.removeEventListener('resize', updateTruncationState);
        };
    }, [text, textRef, updateTruncationState]);

    return { isTruncated, updateTruncationState };
};

const TruncatedTooltipText: React.FC<{
    align?: 'center' | 'end' | 'start';
    className?: string;
    side?: 'bottom' | 'left' | 'right' | 'top';
    text: string;
}> = ({ align = 'start', className, side = 'top', text }) => {
    const textRef = useRef<HTMLSpanElement>(null);
    const { isTruncated, updateTruncationState } = useIsTextTruncated(textRef, text);
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);

    const handleOpen = () => {
        setIsTooltipOpen(updateTruncationState());
    };
    const handleClose = () => {
        setIsTooltipOpen(false);
    };

    return (
        <TooltipPrimitive.Provider delayDuration={200}>
            <TooltipPrimitive.Root open={isTooltipOpen && isTruncated}>
                <TooltipPrimitive.Trigger asChild>
                    <span
                        className={clsx(className, isTruncated && 'cursor-help')}
                        onBlur={handleClose}
                        onFocus={handleOpen}
                        onMouseEnter={handleOpen}
                        onMouseLeave={handleClose}
                        ref={textRef}
                    >
                        {text}
                    </span>
                </TooltipPrimitive.Trigger>
                {isTruncated ? (
                    <TooltipPrimitive.Portal>
                        <TooltipPrimitive.Content
                            align={align}
                            avoidCollisions
                            className={AUDIT_TOOLTIP_CONTENT_CLASS}
                            collisionPadding={8}
                            side={side}
                            sideOffset={8}
                            sticky="partial"
                        >
                            {text}
                        </TooltipPrimitive.Content>
                    </TooltipPrimitive.Portal>
                ) : null}
            </TooltipPrimitive.Root>
        </TooltipPrimitive.Provider>
    );
};

const PlatformLogoMark: React.FC<{
    isDecorative?: boolean;
    platform: string;
    size?: 'md' | 'sm';
}> = ({ isDecorative = false, platform, size = 'sm' }) => {
    const displayPlatform = getAuditApplicationDisplayName(platform);
    const frameSize = size === 'md' ? 'h-5 w-5' : 'h-[18px] w-[18px]';
    const isGA = displayPlatform === 'Google Analytics';
    const isGoogleAds = displayPlatform === 'Google Ads';
    const imageSize = size === 'md'
        ? (isGA ? 'h-5 w-5 scale-[1.3]' : isGoogleAds ? 'h-[18px] w-[18px] scale-[1.12]' : 'h-4 w-4')
        : (isGA ? 'h-[18px] w-[18px] scale-[1.35]' : isGoogleAds ? 'h-4 w-4 scale-[1.12]' : 'h-[14px] w-[14px]');

    return (
        <AuditTooltip content={displayPlatform}>
            <span className={clsx('inline-flex shrink-0 items-center justify-center overflow-hidden rounded-sm bg-transparent', frameSize)}>
                <img
                    alt={isDecorative ? '' : displayPlatform}
                    aria-hidden={isDecorative || undefined}
                    className={clsx('object-contain', imageSize)}
                    onError={(event) => {
                        event.currentTarget.src = 'https://cdn.brandfetch.io/google.com/icon';
                    }}
                    src={getBrandIcon(displayPlatform)}
                />
            </span>
        </AuditTooltip>
    );
};

const PlatformChip: React.FC<{ platform: string }> = ({ platform }) => {
    const displayPlatform = getAuditApplicationDisplayName(platform);
    const isGA = displayPlatform === 'Google Analytics';
    const isGoogleAds = displayPlatform === 'Google Ads';
    const imageSize = isGA
        ? 'h-[18px] w-[18px] scale-[1.35]'
        : isGoogleAds
            ? 'h-4 w-4 scale-[1.12]'
            : 'h-[14px] w-[14px]';

    return (
        <AuditTooltip content={displayPlatform}>
            <span className="inline-flex max-w-full min-w-0 shrink-0 items-center gap-1.5 text-[11px] font-medium leading-4 text-[#1A162F]">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                    <img
                        alt=""
                        aria-hidden="true"
                        className={clsx('object-contain', imageSize)}
                        onError={(event) => {
                            event.currentTarget.src = 'https://cdn.brandfetch.io/google.com/icon';
                        }}
                        src={getBrandIcon(displayPlatform)}
                    />
                </span>
                <span className="min-w-0 max-w-[150px] truncate">{displayPlatform}</span>
            </span>
        </AuditTooltip>
    );
};

const PlatformChipList: React.FC<{ maxVisible?: number; platforms: string[] }> = ({
    maxVisible = MAX_VISIBLE_PLATFORM_ITEMS,
    platforms,
}) => {
    const uniquePlatforms = uniqueSorted(platforms);
    const shouldCollapse = uniquePlatforms.length > maxVisible;
    const visiblePlatforms = shouldCollapse
        ? uniquePlatforms.slice(0, Math.max(0, maxVisible - 1))
        : uniquePlatforms.slice(0, maxVisible);
    const hiddenPlatforms = shouldCollapse
        ? uniquePlatforms.slice(Math.max(0, maxVisible - 1))
        : [];

    if (!visiblePlatforms.length) return null;

    return (
        <span aria-label={uniquePlatforms.join(', ')} className="inline-flex max-w-full min-w-0 flex-wrap items-center gap-3">
            {visiblePlatforms.map((platform) => <PlatformChip key={platform} platform={platform} />)}
            {hiddenPlatforms.length > 0 ? (
                <AuditTooltip content={hiddenPlatforms.join(' / ')}>
                    <span className="inline-flex shrink-0 items-center text-[11px] font-medium leading-4 text-[#8A93B2]">...</span>
                </AuditTooltip>
            ) : null}
        </span>
    );
};

const PlatformLogoGroup: React.FC<{ maxVisible?: number; platforms: string[]; size?: 'md' | 'sm' }> = ({
    maxVisible = MAX_VISIBLE_PLATFORM_ITEMS,
    platforms,
    size = 'sm',
}) => {
    const uniquePlatforms = uniqueSorted(platforms);
    const shouldCollapse = uniquePlatforms.length > maxVisible;
    const visiblePlatforms = shouldCollapse
        ? uniquePlatforms.slice(0, Math.max(0, maxVisible - 1))
        : uniquePlatforms.slice(0, maxVisible);
    const hiddenPlatforms = shouldCollapse
        ? uniquePlatforms.slice(Math.max(0, maxVisible - 1))
        : [];

    if (!visiblePlatforms.length) return null;

    return (
        <span aria-label={uniquePlatforms.join(', ')} className="inline-flex shrink-0 items-center gap-1">
            {visiblePlatforms.map((platform) => <PlatformLogoMark key={platform} platform={platform} size={size} />)}
            {hiddenPlatforms.length > 0 ? (
                <AuditTooltip content={hiddenPlatforms.join(' / ')}>
                    <span className="inline-flex h-[18px] shrink-0 items-center px-0.5 text-[11px] font-medium leading-none text-[#8A93B2]">...</span>
                </AuditTooltip>
            ) : null}
        </span>
    );
};

const AuditFilterDropdown: React.FC<{
    ariaLabel: string;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    onSelect: (values: string[]) => void;
    options: AuditFilterOption[];
    selectedValues: string[];
    t: Translate;
}> = ({
    ariaLabel,
    isOpen,
    onOpenChange,
    onSelect,
    options,
    selectedValues,
    t,
}) => {
    const isAllSelected = isAllFilterSelected(selectedValues);
    const activeValues = selectedFilterValues(selectedValues);
    const allIndividualValues = useMemo(() => (
        options.filter((option) => option.value !== AUDIT_FILTER_ALL).map((option) => option.value)
    ), [options]);

    let displayLabel = options[0]?.label || ariaLabel;
    let displayPlatform: string | undefined;

    if (!isAllSelected) {
        if (activeValues.length === 1) {
            const option = options.find((item) => item.value === activeValues[0]);
            if (option) {
                displayLabel = option.label;
                displayPlatform = option.platform;
            }
        } else {
            displayLabel = t('website.auditLog.filterSelectedCount', { count: activeValues.length });
        }
    }

    const hasActiveSelection = !isAllSelected && !selectedValues.includes(AUDIT_FILTER_NONE);

    const handleOptionSelect = (value: string, event: Event) => {
        event.preventDefault();

        if (value === AUDIT_FILTER_ALL) {
            onSelect(isAllSelected ? [AUDIT_FILTER_NONE] : []);
            return;
        }

        const currentActive = isAllSelected
            ? allIndividualValues
            : activeValues.filter((item) => item !== AUDIT_FILTER_NONE);

        const nextValues = currentActive.includes(value)
            ? currentActive.filter((item) => item !== value)
            : [...currentActive, value];

        if (nextValues.length === 0) {
            onSelect([AUDIT_FILTER_NONE]);
        } else if (nextValues.length === allIndividualValues.length) {
            onSelect([]);
        } else {
            onSelect(nextValues);
        }
    };

    return (
        <DropdownMenu.Root open={isOpen} onOpenChange={onOpenChange}>
            <DropdownMenu.Trigger asChild>
                <button
                    aria-label={`${ariaLabel}: ${displayLabel}`}
                    className={clsx(
                        'inline-flex h-9 w-[180px] items-center justify-between gap-3 rounded-[10px] border bg-white px-3 py-1.5 text-left text-sm font-normal text-[#1A162F] transition-all hover:border-[#C8D0E2] focus:border-[#1A162F] focus:outline-none focus:ring-1 focus:ring-[#1A162F]',
                        hasActiveSelection ? 'border-[#C8D0E2] bg-[#F8F9FC]' : 'border-[#E8EBF3]',
                    )}
                    type="button"
                >
                    <span className="flex min-w-0 items-center gap-2">
                        {displayPlatform ? <PlatformLogoMark isDecorative platform={displayPlatform} size="sm" /> : null}
                        <TruncatedTooltipText className="min-w-0 truncate" text={displayLabel} />
                    </span>
                    <CaretDown
                        className={clsx('shrink-0 text-[#8A93B2] transition-transform duration-200', isOpen && 'rotate-180')}
                        size={14}
                        weight="regular"
                    />
                </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    align="end"
                    className="z-[10002] w-[180px] origin-top overflow-hidden rounded-xl border border-[#E8EBF3] bg-white shadow-[0_16px_40px_rgba(26,22,47,0.12)] animate-in fade-in zoom-in-95 duration-100"
                    sideOffset={6}
                >
                    <div className="custom-scrollbar max-h-[320px] space-y-0.5 overflow-y-auto p-1">
                        {options.map((option) => {
                            const isSelected = isAllSelected || activeValues.includes(option.value);
                            return (
                                <DropdownMenu.Item
                                    className={clsx(
                                        'group flex cursor-pointer items-center rounded-[8px] px-2.5 py-2 text-sm font-normal outline-none transition-colors hover:bg-[#F8F9FC] focus:bg-[#F8F9FC]',
                                        isSelected ? 'text-[#1A162F]' : 'text-[#666F8D] hover:text-[#1A162F]',
                                    )}
                                    key={option.value}
                                    onSelect={(event) => handleOptionSelect(option.value, event)}
                                >
                                    <div
                                        className={clsx(
                                            'mr-2.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors duration-100',
                                            isSelected
                                                ? 'border-[#1A162F] bg-[#1A162F] text-white'
                                                : 'border-[#C8D0E2] bg-white text-transparent group-hover:border-[#8A93B2]',
                                        )}
                                    >
                                        <Check className="shrink-0" size={10} weight="bold" />
                                    </div>
                                    <span className="flex min-w-0 items-center gap-2 pr-2">
                                        {option.platform ? <PlatformLogoMark isDecorative platform={option.platform} size="sm" /> : null}
                                        <TruncatedTooltipText className="min-w-0 truncate" text={option.label} />
                                    </span>
                                </DropdownMenu.Item>
                            );
                        })}
                    </div>
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
};

const ReviewTimelineItem = React.forwardRef((
    {
        event,
        isHighlighted,
        isLast,
        locale,
        onViewContent,
        t,
    }: {
        event: CloudAgentRunAuditTimelineEventView;
        isHighlighted?: boolean;
        isLast: boolean;
        locale: LocaleCode;
        onViewContent: () => void;
        t: Translate;
    },
    ref: React.Ref<HTMLDivElement>,
) => {
    const platforms = event.platformLabel ? [event.platformLabel] : [];
    const actionLabel = event.applicationAccess === 'write'
        ? t('website.auditLog.writeApplication')
        : event.applicationAccess === 'read'
            ? t('website.auditLog.readApplication')
            : event.operationLabel || t('website.auditLog.action');
    const metadataLabel = event.resourceLabel || event.connectionDisplayName || event.statusLabel;

    return (
        <div ref={ref} className="relative grid gap-x-3 pb-6 last:pb-0" style={{ gridTemplateColumns: '60px 20px minmax(0, 1fr)' }}>
            {!isLast ? (
                <div className="absolute bottom-[-36px] top-[24px] w-[1.5px] bg-[#E8EBF3]" style={{ left: '81.25px' }} />
            ) : null}
            <div className="select-none whitespace-nowrap pr-1 pt-3 text-right text-[11px] font-medium leading-5 tabular-nums text-[#9AA3B8]">
                {formatStepTime(event.eventAt, locale)}
            </div>
            <div className="relative z-10 pt-3">
                <div className="flex h-4 w-5 items-center justify-center">
                    <div className={clsx(
                        'box-content h-1.5 w-1.5 rounded-full border-[3.5px] border-white',
                        event.applicationAccess === 'write' ? 'bg-amber-500' : 'bg-[#A2AED0]',
                    )} />
                </div>
            </div>
            <div className={clsx('-mx-3 min-w-0 rounded-xl px-3 py-3 transition-all duration-700', isHighlighted ? 'bg-[#F0F4FF]' : 'bg-transparent')}>
                <div className="flex min-w-0 items-center gap-1.5">
                    <TruncatedTooltipText
                        className="block min-w-0 truncate text-[12px] font-medium leading-5 text-[#1A162F]"
                        text={event.actionLabel}
                    />
                </div>
                {event.description ? (
                    <p className="mt-0.5 text-[11px] leading-relaxed text-[#666F8D]">{event.description}</p>
                ) : null}
                <div className="mt-2 rounded-[8px] border border-[#E8EBF3] bg-[#F8F9FC]/30 p-2.5 text-[11px] leading-relaxed text-[#666F8D]">
                    <div className="flex flex-col gap-y-1.5 sm:flex-row sm:items-center sm:justify-start sm:gap-x-10">
                        <div className={clsx('flex min-w-0 items-center gap-1.5', platforms.length ? 'flex-wrap' : 'truncate')}>
                            <span className="shrink-0 text-[#9AA3B8]">{t('website.auditLog.involvedApps')}:</span>
                            {platforms.length ? (
                                <PlatformChipList platforms={platforms} />
                            ) : (
                                <TruncatedTooltipText className="min-w-0 truncate text-[#1A162F]" text={metadataLabel || '-'} />
                            )}
                        </div>
                        <div className="flex min-w-0 items-center gap-1.5 truncate">
                            <span className="shrink-0 text-[#9AA3B8]">{t('website.auditLog.action')}:</span>
                            <TruncatedTooltipText className="min-w-0 truncate text-[#1A162F]" text={actionLabel} />
                        </div>
                    </div>
                    {event.changedFields.length > 0 ? (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {event.changedFields.map((field) => (
                                <span key={field} className="rounded-md bg-white px-2 py-0.5 text-[10px] font-medium text-[#4D4865] ring-1 ring-[#E8EBF3]">
                                    {field}
                                </span>
                            ))}
                        </div>
                    ) : null}
                </div>
                {event.contentAvailability === 'available' ? (
                    <button
                        className="mt-1.5 inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-[11px] font-medium text-[#4F7BE8] transition-colors hover:bg-[#F0F4FF] hover:text-[#3560C7]"
                        onClick={onViewContent}
                        type="button"
                    >
                        {t('website.auditLog.viewContentDetails')}
                        <ArrowRight size={11} weight="bold" />
                    </button>
                ) : null}
            </div>
        </div>
    );
});
ReviewTimelineItem.displayName = 'ReviewTimelineItem';

const ConversationMessage = React.forwardRef((
    {
        agentName,
        isHighlighted,
        locale,
        message,
        t,
        userName,
    }: {
        agentName?: string;
        isHighlighted?: boolean;
        locale: LocaleCode;
        message: CloudAgentRunAuditConversationMessageView;
        t: Translate;
        userName?: string;
    },
    ref: React.Ref<HTMLDivElement>,
) => {
    const { user } = useAuth();
    const normalizedRole = message.role.toLowerCase();
    const isUser = normalizedRole === 'user' || normalizedRole === 'member';
    const currentUserAvatarUrl = getUserAvatarUrl(user);
    const messageTime = formatStepTime(message.messageAt, locale);
    const highlightClass = clsx('-mx-3 rounded-xl px-3 py-2 transition-all duration-700', isHighlighted ? 'bg-[#F0F4FF]' : 'bg-transparent');

    if (isUser) {
        return (
            <div ref={ref} className={clsx('flex w-full justify-end', highlightClass)}>
                <div className="flex max-w-[85%] flex-row-reverse items-start gap-2.5">
                    <img
                        alt={userName || t('website.auditLog.initiator')}
                        className="mt-0.5 h-7 w-7 shrink-0 rounded-full border border-[#E8EBF3] object-cover"
                        src={currentUserAvatarUrl}
                    />
                    <div className="rounded-2xl rounded-tr-none bg-[#F4F5F8] px-4 py-3 text-[#1A162F]">
                        <div className="mb-1.5 flex items-center justify-end gap-1.5 text-[11px] leading-4 text-[#8A93B2]">
                            <span>{userName || t('website.auditLog.initiator')}</span>
                            <span aria-hidden="true">·</span>
                            <time dateTime={message.messageAt || undefined}>{messageTime}</time>
                        </div>
                        <p className="whitespace-pre-wrap break-words text-sm font-normal leading-6">{message.contentText}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div ref={ref} className={clsx('flex w-full flex-col text-left', highlightClass)}>
            <div className="mb-1.5 flex items-center gap-1.5 text-[11px] leading-4 text-[#8A93B2]">
                <span>{agentName || 'Agent'}</span>
                <span aria-hidden="true">·</span>
                <time dateTime={message.messageAt || undefined}>{messageTime}</time>
            </div>
            <p className="whitespace-pre-wrap break-words text-sm font-normal leading-6 text-[#1A162F]">
                {message.contentText}
            </p>
        </div>
    );
});
ConversationMessage.displayName = 'ConversationMessage';

const ConversationPanel: React.FC<{
    conversationState: ConversationState;
    locale: LocaleCode;
    onRequest: () => void;
    run: CloudAgentRunAuditRunView;
    t: Translate;
    userDisplayName: string;
}> = ({ conversationState, locale, onRequest, run, t, userDisplayName }) => {
    if (!canRequestConversation(run)) {
        return (
            <div className="flex min-h-0 flex-1 items-center justify-center px-6 py-12 text-center">
                <div className="max-w-[420px]">
                    <h3 className="text-base font-medium text-[#1A162F]">{t('website.auditLog.conversationEmptyTitle')}</h3>
                    <p className="mt-2 text-sm font-normal leading-6 text-[#666F8D]">{t('website.auditLog.conversationEmptyBody')}</p>
                </div>
            </div>
        );
    }
    if (conversationState.kind === 'idle') {
        return (
            <div className="flex flex-1 items-center justify-center">
                <button
                    className="inline-flex h-9 items-center gap-2 rounded-lg border border-[#E8EBF3] bg-white px-4 text-[12px] font-medium text-[#1A162F] transition-colors hover:border-[#C8D0E2] hover:bg-[#F8F9FC]"
                    onClick={onRequest}
                    type="button"
                >
                    <ChatCircleText size={15} weight="bold" />
                    {t('website.auditLog.viewOriginalConversation')}
                </button>
            </div>
        );
    }
    if (conversationState.kind === 'loading') {
        return (
            <div aria-busy="true" aria-live="polite" className="flex min-h-0 flex-1 items-center justify-center">
                <BustlyLoadingAnimation />
                <span className="sr-only">{t('website.auditLog.conversationLoading')}</span>
            </div>
        );
    }
    if (conversationState.kind === 'forbidden') return <div className="py-8 text-center text-[12px] text-[#8A93A8]">{t('website.auditLog.conversationForbidden')}</div>;
    if (conversationState.kind === 'unavailable') {
        return (
            <div className="flex min-h-0 flex-1 items-center justify-center px-6 py-12 text-center">
                <div className="max-w-[420px]">
                    <h3 className="text-base font-medium text-[#1A162F]">{t('website.auditLog.conversationEmptyTitle')}</h3>
                    <p className="mt-2 text-sm font-normal leading-6 text-[#666F8D]">{t('website.auditLog.conversationEmptyBody')}</p>
                </div>
            </div>
        );
    }
    if (conversationState.kind === 'error') {
        return (
            <div className="flex flex-col items-center justify-center gap-3 py-8 text-center text-[12px] text-[#8A93A8]">
                <span>{t('website.auditLog.conversationError')}</span>
                <button className="font-medium text-[#1A162F] underline underline-offset-4" onClick={onRequest} type="button">
                    {t('website.auditLog.tryAgain')}
                </button>
            </div>
        );
    }

    if (!conversationState.conversation.messages.length) {
        return (
            <div className="flex min-h-0 flex-1 items-center justify-center px-6 py-12 text-center">
                <div className="max-w-[420px]">
                    <h3 className="text-base font-medium text-[#1A162F]">{t('website.auditLog.conversationEmptyTitle')}</h3>
                    <p className="mt-2 text-sm font-normal leading-6 text-[#666F8D]">{t('website.auditLog.conversationEmptyBody')}</p>
                </div>
            </div>
        );
    }

    const visibleMessages = getVisibleCloudAgentRunAuditConversationMessages(
        conversationState.conversation.messages,
    );
    const memberDisplayName = getDisplayMember(run, userDisplayName);

    if (!visibleMessages.length) {
        return (
            <div className="flex min-h-0 flex-1 items-center justify-center px-6 py-12 text-center">
                <div className="max-w-[420px]">
                    <h3 className="text-base font-medium text-[#1A162F]">{t('website.auditLog.conversationEmptyTitle')}</h3>
                    <p className="mt-2 text-sm font-normal leading-6 text-[#666F8D]">{t('website.auditLog.conversationEmptyBody')}</p>
                </div>
            </div>
        );
    }

    return (
        <section className="custom-scrollbar flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-6 py-6 [scrollbar-gutter:stable]">
            {visibleMessages.map((message) => (
                <ConversationMessage
                    agentName={run.agentLabel || undefined}
                    key={message.id}
                    locale={locale}
                    message={message}
                    t={t}
                    userName={memberDisplayName}
                />
            ))}
        </section>
    );
};

const ContentPanel: React.FC<{
    onRequest: () => void;
    state: ContentState;
    t: Translate;
}> = ({ onRequest, state, t }) => {
    if (state.kind === 'loading') {
        return <div className="flex items-center gap-2 py-3 text-sm text-[#666F8D]"><CircleNotch className="animate-spin" size={16} />{t('website.auditLog.contentLoading')}</div>;
    }
    if (state.kind === 'forbidden') return <p className="py-3 text-sm text-[#666F8D]">{t('website.auditLog.contentForbidden')}</p>;
    if (state.kind === 'unavailable') return <p className="py-3 text-sm text-[#666F8D]">{t('website.auditLog.contentUnavailable')}</p>;
    if (state.kind === 'error') {
        return (
            <div className="flex items-center gap-3 py-3">
                <p className="text-sm text-[#666F8D]">{t('website.auditLog.contentError')}</p>
                <button className="text-sm font-medium text-[#1A162F] underline underline-offset-4" onClick={onRequest} type="button">
                    {t('website.auditLog.tryAgain')}
                </button>
            </div>
        );
    }
    if (state.kind === 'ready') {
        return (
            <pre className="custom-scrollbar max-h-[65vh] overflow-auto rounded-md border border-[#E8EBF3] bg-[#FAFBFD] p-3 text-xs leading-5 text-[#1A162F]">
                {formatPayload(state.content.payload)}
            </pre>
        );
    }
    return null;
};

const AuditDrawer: React.FC<{
    conversationState: ConversationState;
    initialTab: AuditDrawerTab;
    locale: LocaleCode;
    onClose: () => void;
    onCloseStart?: () => void;
    onRequestContent: (eventId: string) => void;
    onRequestConversation: () => void;
    run: CloudAgentRunAuditRunView;
    targetItemId?: string;
    t: Translate;
    userDisplayName: string;
}> = ({
    conversationState,
    initialTab,
    locale,
    onClose,
    onCloseStart,
    onRequestContent,
    onRequestConversation,
    run,
    targetItemId,
    t,
    userDisplayName,
}) => {
    const [activeTab, setActiveTab] = useState<AuditDrawerTab>(initialTab);
    const [isMounted, setIsMounted] = useState(false);
    const [isAnimatingOut, setIsAnimatingOut] = useState(false);
    const [highlightedTimelineId, setHighlightedTimelineId] = useState<string | null>(targetItemId || null);
    const closeTimerRef = useRef<number | null>(null);
    const timelineRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());
    const timelineHighlightTimerRef = useRef<number | null>(null);
    const scrolledTargetRef = useRef<string | null>(null);

    const registerTimelineRef = useCallback((id: string, element: HTMLDivElement | null) => {
        if (element) {
            timelineRefsMap.current.set(id, element);
        } else {
            timelineRefsMap.current.delete(id);
        }
    }, []);

    useEffect(() => {
        if (targetItemId && activeTab === 'timeline' && scrolledTargetRef.current !== targetItemId) {
            scrolledTargetRef.current = targetItemId;
            setHighlightedTimelineId(targetItemId);

            const frame = requestAnimationFrame(() => {
                timelineRefsMap.current.get(targetItemId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });

            if (timelineHighlightTimerRef.current !== null) {
                window.clearTimeout(timelineHighlightTimerRef.current);
            }
            timelineHighlightTimerRef.current = window.setTimeout(() => {
                setHighlightedTimelineId(null);
            }, 2500);

            return () => cancelAnimationFrame(frame);
        }
        return undefined;
    }, [activeTab, targetItemId]);

    useEffect(() => {
        const timer = window.setTimeout(() => setIsMounted(true), 16);
        return () => window.clearTimeout(timer);
    }, []);

    const handleClose = () => {
        if (isAnimatingOut) return;
        onCloseStart?.();
        setIsMounted(false);
        setIsAnimatingOut(true);
        closeTimerRef.current = window.setTimeout(onClose, 300);
    };

    useEffect(() => () => {
        if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current);
        if (timelineHighlightTimerRef.current !== null) window.clearTimeout(timelineHighlightTimerRef.current);
    }, []);

    const timelinePanel = (
        <div className="custom-scrollbar h-full min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-6 [scrollbar-gutter:stable]">
            <section className="flex flex-col">
                <div className="flex flex-wrap items-center gap-1.5 border-b border-[#F1F4FA] pb-3 text-[11px] leading-5">
                    <span className="text-[#8A93B2]">{t('website.auditLog.executionTime')}:</span>
                    <span className="font-medium text-[#1A162F]">{formatDateTime(run.startedAt || run.lastEventAt, locale)}</span>
                    <span className="text-[#BFC6D4]">·</span>
                    <span className="text-[#666F8D]">{getRunActionSummary(run, t)}</span>
                </div>
                {run.timeline.length ? (
                    <div className="mt-4 space-y-0 pb-1">
                        {run.timeline.map((event, index) => (
                            <ReviewTimelineItem
                                event={event}
                                isHighlighted={highlightedTimelineId === event.id}
                                isLast={index === run.timeline.length - 1}
                                key={event.id}
                                locale={locale}
                                onViewContent={() => onRequestContent(event.id)}
                                ref={(element) => registerTimelineRef(event.id, element)}
                                t={t}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="py-8 text-center text-[12px] text-[#8A93A8]">{t('website.auditLog.noBusinessEvents')}</div>
                )}
            </section>
        </div>
    );

    const chatPanel = (
        <ConversationPanel
            conversationState={conversationState}
            locale={locale}
            onRequest={onRequestConversation}
            run={run}
            t={t}
            userDisplayName={userDisplayName}
        />
    );

    return createPortal(
        <>
            <button
                aria-label={t('website.auditLog.closeAuditDetails')}
                className={clsx(
                    'fixed inset-0 z-[500] cursor-default transition-[opacity,background-color,backdrop-filter] ease-[cubic-bezier(0.16,1,0.3,1)]',
                    isMounted
                        ? 'bg-[#1A162F]/20 opacity-100 backdrop-blur-[1px] duration-200'
                        : 'bg-[#F4F5F8]/0 opacity-0 backdrop-blur-0 duration-150',
                )}
                onClick={handleClose}
                type="button"
            />
            <aside
                className={clsx(
                    'fixed bottom-0 right-0 top-0 z-[510] flex w-[1040px] max-w-[98vw] flex-col overflow-hidden rounded-l-[24px] border-l border-[#E8EBF3] bg-white transition-[transform,box-shadow] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]',
                    isMounted
                        ? 'translate-x-0 shadow-[-24px_0_60px_rgba(26,22,47,0.12)]'
                        : 'translate-x-full shadow-[0_0_0_rgba(26,22,47,0)]',
                )}
            >
                <header className="px-6 py-5">
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                            <div className="flex min-w-0 flex-wrap items-center gap-2">
                                <h3 className="min-w-0 max-w-[760px] text-[18px] font-medium leading-7 text-[#1A162F]">
                                    <TruncatedTooltipText className="block min-w-0 truncate" text={run.title} />
                                </h3>
                                <span className={clsx('inline-flex h-6 shrink-0 items-center rounded-full px-2.5 text-[12px] font-medium leading-none', statusBadgeClassName(run.statusLabel))}>
                                    {run.statusLabel}
                                </span>
                            </div>
                        </div>
                        <button
                            aria-label={t('website.auditLog.closeAuditDetails')}
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-[#8A93B2] transition-colors hover:bg-[#F4F5F8] hover:text-[#1A162F]"
                            onClick={handleClose}
                            type="button"
                        >
                            <X size={16} weight="bold" />
                        </button>
                    </div>
                </header>
                <div className="mx-6 flex h-12 shrink-0 items-center border-b border-[#ECEFF5] bg-white">
                    <div className="flex h-full items-end gap-6">
                        {auditDrawerTabs.map((tab) => {
                            const isActive = activeTab === tab.value;
                            return (
                                <button
                                    className={clsx(
                                        'h-full border-b-2 px-1 text-sm font-medium transition-colors',
                                        isActive
                                            ? 'border-[#1A162F] text-[#1A162F]'
                                            : 'border-transparent text-[#666F8D] hover:text-[#1A162F]',
                                    )}
                                    key={tab.value}
                                    onClick={() => {
                                        setActiveTab(tab.value);
                                        if (tab.value === 'conversation' && canRequestConversation(run) && conversationState.kind === 'idle') {
                                            onRequestConversation();
                                        }
                                    }}
                                    type="button"
                                >
                                    {tab.value === 'conversation' && conversationState.kind === 'ready' && conversationState.conversation.messageCount > 0
                                        ? t('website.auditLog.originalConversationWithCount', { count: conversationState.conversation.messageCount })
                                        : t(tab.label)}
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="flex min-h-0 flex-1 overflow-hidden bg-white">
                    {activeTab === 'timeline' ? timelinePanel : chatPanel}
                </div>
            </aside>
        </>,
        document.body,
    );
};

const AuditRecordCard: React.FC<{
    isSelected: boolean;
    locale: LocaleCode;
    onOpen: (tab: AuditDrawerTab, targetItemId?: string) => void;
    run: CloudAgentRunAuditRunView;
    t: Translate;
    userDisplayName: string;
}> = ({ isSelected, locale, onOpen, run, t, userDisplayName }) => {
    const { user } = useAuth();
    const currentUserAvatarUrl = getUserAvatarUrl(user);
    const displayMember = getDisplayMember(run, userDisplayName);
    const runPlatforms = getRunAuditApplications(run);
    const visibleLogs = run.timeline;

    return (
        <article
            className={clsx(
                'group relative flex w-full min-w-0 flex-col overflow-hidden rounded-xl border p-5 text-left transition-colors duration-150',
                isSelected
                    ? 'border-[#1A162F] bg-white shadow-none'
                    : 'border-[#E8EBF3] bg-white shadow-none hover:border-[#BFC6D4]',
            )}
        >
            <button
                aria-label={`${t('website.auditLog.viewDetails')}: ${run.title}`}
                className="absolute inset-0 z-0 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20"
                onClick={() => onOpen('timeline')}
                type="button"
            />
            <div className="relative z-[1] flex min-w-0 cursor-pointer items-center justify-between gap-4" onClick={() => onOpen('timeline')}>
                <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center justify-between gap-4">
                        <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <h3 className="max-w-[680px] text-[13px] font-semibold leading-5 text-[#1A162F] [overflow-wrap:anywhere]">
                                <TruncatedTooltipText className="block min-w-0 truncate" text={run.title} />
                            </h3>
                            <span className={clsx('inline-flex h-6 shrink-0 items-center rounded-full px-2.5 text-[11px] font-medium leading-none', statusBadgeClassName(run.statusLabel))}>
                                {run.statusLabel}
                            </span>
                        </div>
                        {canRequestConversation(run) ? (
                            <button
                                className="relative z-[2] inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-[#E8EBF3] bg-white px-3 text-[12px] font-medium text-[#666F8D] transition-colors hover:border-[#C8D0E2] hover:bg-[#F8F9FC] hover:text-[#1A162F] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onOpen('conversation');
                                }}
                                type="button"
                            >
                                <ChatCircleText size={15} weight="bold" />
                                {t('website.auditLog.viewOriginalConversation')}
                            </button>
                        ) : null}
                    </div>
                    <div className="mt-4 grid grid-cols-3 gap-x-8 gap-y-3.5 border-t border-[#F1F4F9] pt-4 text-[12px] leading-5 text-[#667085]">
                        <div className="flex min-w-0 items-center">
                            <span className="w-24 shrink-0 text-[#8A93B2]">{t('website.auditLog.initiator')}:</span>
                            <div className="flex min-w-0 items-center gap-1.5">
                                <img alt={displayMember} className="h-5 w-5 shrink-0 rounded-full border border-[#E8EBF3] object-cover" src={currentUserAvatarUrl} />
                                <TruncatedTooltipText className="min-w-0 truncate font-medium text-[#1A162F]" text={displayMember} />
                            </div>
                        </div>
                        <div className="flex min-w-0 items-center">
                            <span className="w-24 shrink-0 text-[#8A93B2]">Agent:</span>
                            <TruncatedTooltipText className="min-w-0 truncate font-medium text-[#1A162F]" text={run.agentLabel || '-'} />
                        </div>
                        <div className="flex min-w-0 items-center">
                            <span className="w-24 shrink-0 text-[#8A93B2]">{t('website.auditLog.involvedApps')}:</span>
                            {runPlatforms.length ? (
                                <PlatformLogoGroup platforms={runPlatforms} size="sm" />
                            ) : (
                                <span className="font-medium text-[#8A93B2]">{t('website.auditLog.pendingLocation')}</span>
                            )}
                        </div>
                        <div className="flex min-w-0 items-center">
                            <span className="w-24 shrink-0 text-[#8A93B2]">{t('website.auditLog.executionTime')}:</span>
                            <TruncatedTooltipText className="min-w-0 truncate font-medium text-[#1A162F]" text={formatDateTime(run.startedAt || run.lastEventAt, locale)} />
                        </div>
                        <div className="flex min-w-0 items-center">
                            <span className="w-24 shrink-0 text-[#8A93B2]">{t('website.auditLog.sourceTask')}:</span>
                            <TruncatedTooltipText className="min-w-0 truncate font-medium text-[#1A162F]" text={run.sourceTask || '-'} />
                        </div>
                        <div className="flex min-w-0 items-center">
                            <span className="w-24 shrink-0 text-[#8A93B2]">{t('website.auditLog.keyActions')}:</span>
                            <TruncatedTooltipText className="min-w-0 truncate font-medium text-[#1A162F]" text={getRunActionSummary(run, t)} />
                        </div>
                    </div>
                    {visibleLogs.length > 0 ? (
                        <div className="relative z-[2] mt-3 border-t border-[#F1F4F9] pt-3" onClick={(event) => event.stopPropagation()}>
                            <div className="space-y-1.5">
                                {visibleLogs.map((event) => (
                                    <button
                                        className="group/log -mx-2 flex w-full min-w-0 items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[11px] leading-5 transition-all hover:bg-[#F4F5F8]"
                                        key={event.id}
                                        onClick={() => onOpen('timeline', event.id)}
                                        type="button"
                                    >
                                        <span className="w-[60px] shrink-0 select-none whitespace-nowrap text-left text-[10px] font-medium tabular-nums text-[#9AA3B8]">
                                            {formatStepTime(event.eventAt, locale)}
                                        </span>
                                        <span className={clsx('mx-0.5 h-1.5 w-1.5 shrink-0 rounded-full transition-colors', event.applicationAccess === 'write' ? 'bg-amber-500' : 'bg-[#A2AED0]')} />
                                        <span className="min-w-0 flex-1 truncate text-[#666F8D] transition-colors group-hover/log:text-[#1A162F]">
                                            {event.actionLabel}
                                        </span>
                                        <ArrowRight className="shrink-0 text-[#8A93B2] opacity-0 transition-opacity duration-200 group-hover/log:opacity-100" size={11} weight="bold" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </article>
    );
};

const CloudAgentRunAuditPage: React.FC = () => {
    const { currentWorkspace, permissions, user } = useAuth();
    const { locale, t } = useTranslation();
    const workspaceId = currentWorkspace?.id || null;
    const canViewAuditLog = canViewCloudAgentRunAudit(permissions);
    const [pageState, setPageState] = useState<AuditPageState>({ kind: 'loading' });
    const [refreshToken, setRefreshToken] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTimeRange, setSelectedTimeRange] = useState<AuditLogTimeRange>(DEFAULT_AUDIT_LOG_TIME_RANGE);
    const [selectedApplications, setSelectedApplications] = useState<string[]>([]);
    const [selectedInitiators, setSelectedInitiators] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCursors, setPageCursors] = useState<Array<string | null>>([null]);
    const [paginationWorkspaceId, setPaginationWorkspaceId] = useState<string | null>(workspaceId);
    const [selectedDrawer, setSelectedDrawer] = useState<{
        initialTab: AuditDrawerTab;
        runId: string;
        targetItemId?: string;
    } | null>(null);
    const [isDrawerClosing, setIsDrawerClosing] = useState(false);
    const [openFilter, setOpenFilter] = useState<AuditFilterKind | null>(null);
    const [contentOpen, setContentOpen] = useState(false);
    const [contentState, setContentState] = useState<ContentState>({ kind: 'idle' });
    const [conversationState, setConversationState] = useState<ConversationState>({ kind: 'idle' });
    const auditTimeZone = useMemo(resolveAuditLogTimeZone, []);
    const currentPageCursor = pageCursors[currentPage - 1] || undefined;
    const hasPaginationForCurrentWorkspace = paginationWorkspaceId === workspaceId;

    useEffect(() => {
        setCurrentPage(1);
        setPageCursors([null]);
        setPaginationWorkspaceId(workspaceId);
    }, [workspaceId]);

    useEffect(() => {
        let disposed = false;

        if (!hasPaginationForCurrentWorkspace) {
            setPageState({ kind: 'loading' });
            return () => { disposed = true; };
        }
        if (!workspaceId) {
            setPageState({ kind: 'workspace-unavailable' });
            return () => { disposed = true; };
        }
        if (permissions.isLoading) {
            setPageState({ kind: 'loading' });
            return () => { disposed = true; };
        }
        if (!canViewAuditLog) {
            setPageState({ kind: 'forbidden' });
            return () => { disposed = true; };
        }

        const loadRuns = async () => {
            setPageState({ kind: 'loading' });
            const timeWindow = getAuditLogTimeWindow(selectedTimeRange, new Date(), auditTimeZone);
            const listOptions = {
                limit: AUDIT_LOG_PAGE_SIZE,
                ...(currentPageCursor ? { cursor: currentPageCursor } : {}),
                ...timeWindow,
            };
            try {
                const response = await agentRunLedgerService.listRuns(workspaceId, listOptions);
                if (!hasCloudAuditRunPaginationMetadata(response) && !currentPageCursor) {
                    const legacyResponse = await agentRunLedgerService.listRuns(workspaceId, {
                        limit: LEGACY_AUDIT_RUN_LIMIT,
                        ...timeWindow,
                    });
                    if (!disposed) {
                        setCurrentPage(1);
                        setPageCursors([null]);
                        setPageState({
                            kind: 'ready',
                            data: adaptCloudAgentRunAuditRunList(legacyResponse),
                            pagination: { kind: 'local' },
                        });
                    }
                    return;
                }
                if (!disposed) {
                    setPageState({
                        kind: 'ready',
                        data: adaptCloudAgentRunAuditRunList(response),
                        pagination: {
                            kind: 'cursor',
                            nextCursor: response.hasMore === false ? null : response.nextCursor || null,
                        },
                    });
                }
            } catch (error: unknown) {
                if (disposed) return;
                if (shouldFallbackToEventList(error)) {
                    try {
                        const response = await agentRunLedgerService.listEvents(workspaceId, {
                            limit: FALLBACK_AUDIT_EVENT_LIMIT,
                            ...timeWindow,
                        });
                        if (!disposed) {
                            setCurrentPage(1);
                            setPageCursors([null]);
                            setPageState({
                                kind: 'ready',
                                data: adaptCloudAgentRunAuditEventListToRunList(response),
                                pagination: { kind: 'local' },
                            });
                        }
                    } catch (fallbackError: unknown) {
                        if (disposed) return;
                        setPageState(getHttpStatus(fallbackError) === 403
                            ? { kind: 'forbidden' }
                            : { kind: 'error', detail: describeLoadError(fallbackError) });
                    }
                    return;
                }
                setPageState(getHttpStatus(error) === 403 ? { kind: 'forbidden' } : { kind: 'error', detail: describeLoadError(error) });
            }
        };
        void loadRuns();

        return () => { disposed = true; };
    }, [
        auditTimeZone,
        canViewAuditLog,
        currentPageCursor,
        hasPaginationForCurrentWorkspace,
        permissions.isLoading,
        refreshToken,
        selectedTimeRange,
        workspaceId,
    ]);

    const runs = pageState.kind === 'ready' ? pageState.data.runs : [];
    const userDisplayName = user?.user_metadata?.full_name || user?.email || t('website.auditLog.initiator');
    const filteredRuns = useMemo(
        () => filterRuns(
            runs,
            searchQuery,
            selectedTimeRange,
            selectedApplications,
            selectedInitiators,
            auditTimeZone,
        ),
        [auditTimeZone, runs, searchQuery, selectedApplications, selectedInitiators, selectedTimeRange],
    );
    const isLocalPagination = pageState.kind === 'ready' && pageState.pagination.kind === 'local';
    const localPageCount = isLocalPagination
        ? Math.max(1, Math.ceil(filteredRuns.length / AUDIT_LOG_PAGE_SIZE))
        : 1;
    const visibleRuns = useMemo(
        () => isLocalPagination
            ? filteredRuns.slice(
                (currentPage - 1) * AUDIT_LOG_PAGE_SIZE,
                currentPage * AUDIT_LOG_PAGE_SIZE,
            )
            : filteredRuns,
        [currentPage, filteredRuns, isLocalPagination],
    );
    const hasNextAuditPage = pageState.kind === 'ready' && (
        pageState.pagination.kind === 'local'
            ? currentPage < localPageCount
            : Boolean(pageState.pagination.nextCursor)
    );
    const visiblePages = useMemo(
        () => getVisiblePaginationPages(
            currentPage,
            isLocalPagination ? localPageCount : currentPage + (hasNextAuditPage ? 1 : 0),
        ),
        [currentPage, hasNextAuditPage, isLocalPagination, localPageCount],
    );
    const selectedRun = useMemo(
        () => runs.find((run) => run.id === selectedDrawer?.runId) || null,
        [runs, selectedDrawer?.runId],
    );

    const applicationFilterOptions = useMemo<AuditFilterOption[]>(() => [
        { value: AUDIT_FILTER_ALL, label: t('website.auditLog.allApplications') },
        ...getAuditApplicationFilterValues(runs).map((application) => ({
            value: application,
            label: application,
            platform: application,
        })),
    ], [runs, t]);

    const timeRangeOptions = useMemo<AuditTimeRangeOption[]>(() => [
        { value: 'today', label: t('website.auditLog.today') },
        { value: 'last-7-days', label: t('website.auditLog.last7Days') },
        { value: 'last-30-days', label: t('website.auditLog.last30Days') },
        { value: 'last-90-days', label: t('website.auditLog.last90Days') },
        { value: 'all-time', label: t('website.auditLog.allTime') },
    ], [t]);

    const initiatorFilterOptions = useMemo<AuditFilterOption[]>(() => [
        { value: AUDIT_FILTER_ALL, label: t('website.auditLog.allInitiators') },
        ...uniqueSorted(runs.map((run) => run.actorDisplayName)).map((member) => ({
            value: member,
            label: member,
        })),
    ], [runs, t]);

    useEffect(() => {
        if (!isLocalPagination) return;
        setCurrentPage((page) => Math.min(Math.max(1, page), localPageCount));
    }, [isLocalPagination, localPageCount]);

    const requestContent = useCallback((eventId: string) => {
        if (!workspaceId) return;
        setContentOpen(true);
        setContentState({ kind: 'loading', eventId });
        agentRunLedgerService.getEventContent(workspaceId, eventId)
            .then((response) => setContentState({ kind: 'ready', eventId, content: adaptCloudAgentRunAuditContent(response) }))
            .catch((error: unknown) => {
                const status = getHttpStatus(error);
                if (status === 403) setContentState({ kind: 'forbidden', eventId });
                else if (status === 404) setContentState({ kind: 'unavailable', eventId });
                else setContentState({ kind: 'error', eventId });
            });
    }, [workspaceId]);

    const requestConversation = useCallback((runId: string) => {
        if (!workspaceId) return;
        setConversationState({ kind: 'loading', runId });
        agentRunLedgerService.getRunConversation(workspaceId, runId)
            .then((response) => {
                const conversation = adaptCloudAgentRunAuditConversation(response);
                setConversationState({ kind: 'ready', runId, conversation });
                setPageState((previous) => {
                    if (previous.kind !== 'ready') return previous;
                    return {
                        ...previous,
                        data: {
                            ...previous.data,
                            runs: previous.data.runs.map((run) => (run.id === runId
                                ? {
                                    ...run,
                                    actorDisplayName: conversation.actorDisplayName || run.actorDisplayName,
                                    conversationAvailability: 'available',
                                    sourceTask: conversation.sourceTask || run.sourceTask,
                                }
                                : run)),
                        },
                    };
                });
            })
            .catch((error: unknown) => {
                const status = getHttpStatus(error);
                if (status === 403) setConversationState({ kind: 'forbidden', runId });
                else if (status === 404) {
                    setConversationState({ kind: 'unavailable', runId });
                    setPageState((previous) => {
                        if (previous.kind !== 'ready') return previous;
                        return {
                            ...previous,
                            data: {
                                ...previous.data,
                                runs: previous.data.runs.map((run) => (run.id === runId
                                    ? { ...run, conversationAvailability: 'not-captured' }
                                    : run)),
                            },
                        };
                    });
                }
                else setConversationState({ kind: 'error', runId });
            });
    }, [workspaceId]);

    const openRun = (run: CloudAgentRunAuditRunView, initialTab: AuditDrawerTab, targetItemId?: string) => {
        setIsDrawerClosing(false);
        setSelectedDrawer({ initialTab, runId: run.id, targetItemId });
        setConversationState((previous) => (previous.kind !== 'idle' && previous.runId === run.id ? previous : { kind: 'idle' }));
        if (initialTab === 'conversation' && canRequestConversation(run)) {
            requestConversation(run.id);
        }
    };

    const selectedContentEventId = contentState.kind !== 'idle' ? contentState.eventId : null;
    const selectedContentState: ContentState = contentState.kind !== 'idle' && contentState.eventId === selectedContentEventId ? contentState : { kind: 'idle' };
    const conversationForSelectedRun: ConversationState = (
        selectedRun && conversationState.kind !== 'idle' && conversationState.runId === selectedRun.id
            ? conversationState
            : { kind: 'idle' }
    );
    const resetPagination = () => {
        setCurrentPage(1);
        setPageCursors([null]);
    };
    const updateSearchQuery = (value: string) => {
        setSearchQuery(value);
        resetPagination();
    };
    const updateTimeRange = (value: AuditLogTimeRange) => {
        setSelectedTimeRange(value);
        resetPagination();
    };
    const updateApplications = (values: string[]) => {
        setSelectedApplications(values);
        resetPagination();
    };
    const updateInitiators = (values: string[]) => {
        setSelectedInitiators(values);
        resetPagination();
    };
    const clearFilters = () => {
        setSearchQuery('');
        setSelectedTimeRange('all-time');
        setSelectedApplications([]);
        setSelectedInitiators([]);
        resetPagination();
        setOpenFilter(null);
    };
    const goToNextPage = () => {
        const nextCursor = pageState.kind === 'ready' && pageState.pagination.kind === 'cursor'
            ? pageState.pagination.nextCursor
            : null;
        if (!nextCursor) return;
        setPageCursors((previous) => [...previous.slice(0, currentPage), nextCursor]);
        setCurrentPage((page) => page + 1);
    };
    const goToAuditPage = (page: number) => {
        if (page === currentPage || page < 1) return;
        if (isLocalPagination) {
            if (page <= localPageCount) setCurrentPage(page);
            return;
        }
        if (page < currentPage) {
            setCurrentPage(page);
            return;
        }
        if (page === currentPage + 1) goToNextPage();
    };

    return (
        <div className="space-y-2">
            <div className="sticky top-0 z-30 -mx-10 -mr-20 -mt-7 bg-[#F4F5F8] px-10 pb-4 pr-20 pt-7">
                <h2 className="text-[20px] font-medium text-[#1A162F]">{t('website.auditLog.title')}</h2>
                <div className="mt-8 flex flex-wrap items-center justify-between gap-4 px-1">
                    <div className="relative min-w-[260px] max-w-md flex-1">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#8A93B2]">
                        <MagnifyingGlass size={16} weight="bold" />
                    </span>
                    <input
                        className="w-full rounded-[10px] border border-[#E8EBF3] bg-white py-2 pl-9 pr-10 text-sm font-normal text-[#1A162F] transition-all placeholder:text-[#8A93B2] focus:border-[#1A162F] focus:outline-none focus:ring-1 focus:ring-[#1A162F]"
                        onChange={(event) => updateSearchQuery(event.target.value)}
                        placeholder={t('website.auditLog.searchPlaceholder')}
                        type="text"
                        value={searchQuery}
                    />
                    {searchQuery.trim() ? (
                        <button
                            className="absolute right-3 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-[#A0A8C0] transition-colors hover:text-[#1A162F]"
                            onClick={() => updateSearchQuery('')}
                            type="button"
                        >
                            <XCircle size={16} weight="fill" />
                        </button>
                    ) : null}
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-2">
                        <AuditTimeRangeDropdown
                            ariaLabel={t('website.auditLog.filterByTime')}
                            isOpen={openFilter === 'time'}
                            onOpenChange={(open) => setOpenFilter(open ? 'time' : null)}
                            onSelect={updateTimeRange}
                            options={timeRangeOptions}
                            selectedValue={selectedTimeRange}
                        />
                        <AuditFilterDropdown
                        ariaLabel={t('website.auditLog.filterByApplication')}
                        isOpen={openFilter === 'application'}
                        onOpenChange={(open) => setOpenFilter(open ? 'application' : null)}
                        onSelect={updateApplications}
                        options={applicationFilterOptions}
                        selectedValues={selectedApplications}
                        t={t}
                    />
                        <AuditFilterDropdown
                        ariaLabel={t('website.auditLog.filterByInitiator')}
                        isOpen={openFilter === 'initiator'}
                        onOpenChange={(open) => setOpenFilter(open ? 'initiator' : null)}
                        onSelect={updateInitiators}
                        options={initiatorFilterOptions}
                        selectedValues={selectedInitiators}
                        t={t}
                    />
                    </div>
                </div>
            </div>

            {pageState.kind === 'loading' ? <LoadingState /> : null}
            {pageState.kind === 'workspace-unavailable' ? (
                <EmptyState body={t('website.auditLog.workspaceUnavailable')} title={t('website.auditLog.emptyTitle')} />
            ) : null}
            {pageState.kind === 'forbidden' ? (
                <EmptyState body={t('website.auditLog.forbidden')} title={t('website.auditLog.permissionDeniedTitle')} />
            ) : null}
            {pageState.kind === 'error' ? (
                <ErrorState
                    body={pageState.detail ? `${t('website.auditLog.loadError')} (${pageState.detail})` : t('website.auditLog.loadError')}
                    onRetry={() => setRefreshToken((value) => value + 1)}
                    title={t('website.auditLog.loadFailedTitle')}
                    t={t}
                />
            ) : null}
            {pageState.kind === 'ready' && runs.length === 0 ? (
                <EmptyState body={t('website.auditLog.empty')} title={t('website.auditLog.emptyTitle')} />
            ) : null}
            {pageState.kind === 'ready' && runs.length > 0 && visibleRuns.length === 0 ? (
                <EmptyState
                    actionLabel={t('website.auditLog.clearFilters')}
                    body={t('website.auditLog.noMatches')}
                    icon={<MagnifyingGlass size={24} weight="regular" />}
                    onAction={clearFilters}
                    title={t('website.auditLog.noMatchesTitle')}
                />
            ) : null}
            {pageState.kind === 'ready' && runs.length > 0 ? (
                <div className="space-y-4">
                    {visibleRuns.length > 0 ? (
                        <div className="space-y-3">
                            {visibleRuns.map((run) => (
                                <AuditRecordCard
                                    isSelected={selectedDrawer?.runId === run.id && !isDrawerClosing}
                                    key={run.id}
                                    locale={locale}
                                    onOpen={(initialTab, targetItemId) => openRun(run, initialTab, targetItemId)}
                                    run={run}
                                    t={t}
                                    userDisplayName={userDisplayName}
                                />
                            ))}
                        </div>
                    ) : null}
                    <PaginationFooter
                        ariaLabel={t('website.auditLog.pagination')}
                        className="rounded-xl border border-[#E8EBF3]"
                        currentPage={currentPage}
                        getPageLabel={(page) => t('website.auditLog.pageNumber', { page })}
                        hasNextPage={hasNextAuditPage}
                        nextPageLabel={t('website.auditLog.nextPage')}
                        onPageChange={goToAuditPage}
                        pageNumbers={visiblePages}
                        previousPageLabel={t('website.auditLog.previousPage')}
                        summary={t('website.auditLog.showingPageCount', {
                            count: visibleRuns.length,
                            plural: visibleRuns.length === 1 ? '' : 's',
                        })}
                    />
                </div>
            ) : null}

            {selectedRun ? (
                <AuditDrawer
                    conversationState={conversationForSelectedRun}
                    initialTab={selectedDrawer?.initialTab || 'timeline'}
                    key={`${selectedRun.id}:${selectedDrawer?.initialTab || 'timeline'}:${selectedDrawer?.targetItemId || ''}`}
                    locale={locale}
                    onClose={() => {
                        setSelectedDrawer(null);
                        setIsDrawerClosing(false);
                    }}
                    onCloseStart={() => setIsDrawerClosing(true)}
                    onRequestContent={requestContent}
                    onRequestConversation={() => requestConversation(selectedRun.id)}
                    run={selectedRun}
                    targetItemId={selectedDrawer?.targetItemId}
                    t={t}
                    userDisplayName={userDisplayName}
                />
            ) : null}

            {contentOpen ? createPortal(
                <>
                    <button
                        aria-label={t('website.auditLog.close')}
                        className="fixed inset-0 z-[700] cursor-default bg-[#1A162F]/20 backdrop-blur-[1px]"
                        onClick={() => setContentOpen(false)}
                        type="button"
                    />
                    <section className="fixed left-1/2 top-1/2 z-[710] w-[min(680px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-[#E8EBF3] bg-white p-5 shadow-[0_20px_50px_rgba(26,22,47,0.18)]">
                        <div className="flex items-center justify-between gap-4">
                            <h3 className="text-base font-semibold text-[#1A162F]">{t('website.auditLog.contentDetails')}</h3>
                            <button className="text-sm text-[#666F8D]" onClick={() => setContentOpen(false)} type="button">
                                {t('website.auditLog.close')}
                            </button>
                        </div>
                        <div className="mt-4">
                            <ContentPanel
                                onRequest={() => selectedContentEventId && requestContent(selectedContentEventId)}
                                state={selectedContentState}
                                t={t}
                            />
                        </div>
                    </section>
                </>,
                document.body,
            ) : null}
        </div>
    );
};

export default CloudAgentRunAuditPage;
